import { useState } from "react";
import { Trophy, Play, RotateCcw, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Flag } from "../components/Flag";
import { runTournament } from "../lib/api";

const ROUND_NAMES = ["Round of 32", "Round of 16", "Quarter-Finals", "Semi-Finals", "Final"];

export default function Tournament() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = async () => {
    setLoading(true);
    try {
      const result = await runTournament(Math.floor(Math.random() * 1_000_000));
      setData(result);
    } finally {
      setLoading(false);
    }
  };

  const champion = data?.rounds?.[data.rounds.length - 1]?.[0]?.result?.winner;

  return (
    <div className="min-h-screen pb-16">
      <section className="border-b border-zinc-900 bg-zinc-950">
        <div className="mx-auto max-w-[1400px] px-6 py-10">
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-volt">
            Full Tournament Simulation
          </div>
          <h1 className="mt-3 font-display text-4xl font-black uppercase tracking-tighter sm:text-6xl">
            Group Stage <span className="text-volt">+ Knockouts</span>
          </h1>
          <p className="mt-4 max-w-2xl font-body text-sm text-zinc-400">
            48 nations · 12 groups · 72 group matches · 31 knockout ties. Top 2 from each group +
            8 best third-placed teams advance to the Round of 32.
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <Button
              data-testid="run-tournament-btn"
              onClick={run}
              disabled={loading}
              className="rounded-none bg-volt px-6 font-display text-base font-bold uppercase tracking-widest text-black hover:bg-white disabled:opacity-60"
            >
              {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Play className="mr-2 h-4 w-4" />}
              {loading ? "Simulating…" : data ? "Run Again" : "Run Full Tournament"}
            </Button>
            {data && (
              <Button
                onClick={() => setData(null)}
                variant="ghost"
                className="rounded-none font-display text-sm font-bold uppercase tracking-widest text-zinc-400 hover:bg-zinc-900 hover:text-white"
              >
                <RotateCcw className="mr-2 h-4 w-4" /> Reset
              </Button>
            )}
          </div>
        </div>
      </section>

      {champion && (
        <section className="border-b border-zinc-900 bg-volt">
          <div className="mx-auto flex max-w-[1400px] items-center justify-between px-6 py-6 text-black">
            <div className="flex items-center gap-4">
              <Trophy className="h-10 w-10" />
              <div>
                <div className="font-mono text-xs uppercase tracking-[0.3em]">2026 World Champions</div>
                <div className="font-display text-4xl font-black uppercase tracking-tighter">
                  {championName(data, champion)}
                </div>
              </div>
            </div>
            <Flag code={championFlag(data, champion)} className="h-14 w-20 object-cover" />
          </div>
        </section>
      )}

      {!data && !loading && (
        <section className="mx-auto max-w-[1400px] px-6 py-16 text-center">
          <Trophy className="mx-auto h-12 w-12 text-zinc-700" />
          <div className="mt-4 font-display text-2xl font-bold uppercase tracking-tight text-zinc-400">
            Press the button to simulate the entire tournament
          </div>
        </section>
      )}

      {data && (
        <>
          {/* Groups */}
          <section className="mx-auto max-w-[1400px] px-6 py-10">
            <div className="mb-4 font-mono text-xs uppercase tracking-[0.25em] text-zinc-500">
              Group Stage Standings
            </div>
            <div className="grid grid-cols-1 gap-px bg-zinc-800 md:grid-cols-2 xl:grid-cols-3">
              {data.groups.map((g) => (
                <GroupCard key={g.id} group={g} />
              ))}
            </div>
          </section>

          {/* Knockout chain */}
          <section className="max-w-full overflow-x-auto px-6 py-8">
            <div className="mb-4 font-mono text-xs uppercase tracking-[0.25em] text-zinc-500">
              Knockout Chain
            </div>
            <div className="flex min-w-max gap-6" data-testid="knockout-chain">
              {data.rounds.map((round, ri) => (
                <div
                  key={ROUND_NAMES[ri] || `round-${ri}`}
                  className="flex w-60 flex-col justify-around gap-3"
                >
                  <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">
                    {ROUND_NAMES[ri] || `Round ${ri + 1}`}
                  </div>
                  {round.map((m, mi) => (
                    <KnockoutTie
                      key={`${m.a?.code || "tba"}-${m.b?.code || "tba"}-r${ri}-m${mi}`}
                      m={m}
                    />
                  ))}
                </div>
              ))}
            </div>
          </section>
        </>
      )}
    </div>
  );
}

function championName(data, code) {
  for (const round of data.rounds) {
    for (const m of round) {
      if (m.a.code === code) return m.a.name;
      if (m.b.code === code) return m.b.name;
    }
  }
  return code;
}

function championFlag(data, code) {
  for (const round of data.rounds) {
    for (const m of round) {
      if (m.a.code === code) return m.a.flag;
      if (m.b.code === code) return m.b.flag;
    }
  }
  return null;
}

function GroupCard({ group }) {
  return (
    <div className="bg-black p-5" data-testid={`group-${group.id}`}>
      <div className="mb-3 flex items-baseline justify-between">
        <div className="font-display text-2xl font-black uppercase">Group {group.id}</div>
        <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
          GP · W · D · L · GD · PTS
        </div>
      </div>
      <div className="space-y-1.5">
        {group.teams.map((t, idx) => (
          <div
            key={t.code}
            className={`flex items-center justify-between px-2 py-1.5 ${
              idx < 2 ? "bg-zinc-900/60" : idx === 2 ? "bg-zinc-900/30" : ""
            }`}
          >
            <div className="flex items-center gap-2">
              <span
                className={`w-5 text-center font-display text-sm font-black ${
                  idx < 2 ? "text-volt" : "text-zinc-600"
                }`}
              >
                {idx + 1}
              </span>
              <Flag code={t.flag} className="h-3.5 w-5 object-cover" />
              <span
                className={`font-display text-sm font-bold uppercase ${
                  idx < 2 ? "text-white" : "text-zinc-400"
                }`}
              >
                {t.name}
              </span>
            </div>
            <div className="flex gap-2 font-mono text-[11px] tabular-nums text-zinc-400">
              <span>{t.pld}</span>
              <span>{t.w}</span>
              <span>{t.d}</span>
              <span>{t.l}</span>
              <span className={t.gd > 0 ? "text-green-400" : t.gd < 0 ? "text-redz" : ""}>
                {t.gd > 0 ? "+" : ""}{t.gd}
              </span>
              <span className="w-6 text-right font-bold text-white">{t.pts}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function KnockoutTie({ m }) {
  const { a, b, result } = m;
  const aWin =
    result && (result.a > result.b || (result.penalties && result.pens?.a > result.pens?.b));
  const bWin = result && !aWin;
  return (
    <div className="border border-zinc-800 bg-black">
      <KORow team={a} score={result?.a} pen={result?.pens?.a} penalties={result?.penalties} win={aWin} />
      <div className="h-px bg-zinc-900" />
      <KORow team={b} score={result?.b} pen={result?.pens?.b} penalties={result?.penalties} win={bWin} />
    </div>
  );
}

function KORow({ team, score, pen, penalties, win }) {
  if (!team) return <div className="h-9" />;
  return (
    <div className={`flex items-center justify-between px-2.5 py-2 ${win ? "bg-zinc-900/60" : ""}`}>
      <div className="flex items-center gap-1.5 min-w-0">
        <Flag code={team.flag} className="h-3.5 w-5 object-cover" />
        <span
          className={`truncate font-display text-xs font-bold uppercase ${
            win ? "text-volt" : "text-zinc-300"
          }`}
          title={team.name}
        >
          {team.name}
        </span>
      </div>
      <div className="flex items-center gap-1">
        {penalties && pen !== undefined && (
          <span className="font-mono text-[10px] text-zinc-500">({pen})</span>
        )}
        <span
          className={`font-display text-sm font-black tabular-nums ${
            win ? "text-volt" : "text-zinc-500"
          }`}
        >
          {score !== undefined ? score : "–"}
        </span>
      </div>
    </div>
  );
}
